import { useState, useEffect } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { 
  FileText, 
  Plus, 
  Search, 
  Eye,
  Edit2,
  Trash2,
  Filter,
  Clock,
  CheckCircle,
  AlertCircle,
  FileCheck,
  Wrench
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { useTheme } from "@/context/ThemeContext";

const BASE_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Invoices() {
  const { theme } = useTheme();
  const { businessSlug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isDark = theme === "dark";
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || "");
  const [projectSearch, setProjectSearch] = useState(searchParams.get('project') || "");
  const [statusFilter, setStatusFilter] = useState("all");
  const [taxExemptFilter, setTaxExemptFilter] = useState(false);
  
  const API = `${BASE_API}/b/${businessSlug}`;
  const basePath = `/${businessSlug}`;

  // Check URL params for filters
  useEffect(() => {
    const statusParam = searchParams.get('status');
    const taxExemptParam = searchParams.get('tax_exempt');
    
    if (statusParam) {
      setStatusFilter(statusParam);
    }
    if (taxExemptParam === 'true') {
      setTaxExemptFilter(true);
    }
  }, [searchParams]);

  useEffect(() => {
    if (businessSlug) {
      fetchInvoices();
    }
  }, [businessSlug]);

  const fetchInvoices = async () => {
    try {
      const response = await axios.get(`${API}/invoices`);
      setInvoices(response.data);
    } catch (error) {
      toast.error("Failed to fetch invoices");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, invoiceNumber) => {
    if (!window.confirm(`Are you sure you want to delete invoice ${invoiceNumber}?`)) return;
    try {
      await axios.delete(`${API}/invoices/${id}`);
      toast.success("Invoice deleted");
      fetchInvoices();
    } catch (error) {
      toast.error("Failed to delete invoice");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    if (status === 'paid') {
      return (
        <Badge className="bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800">
          PAID
        </Badge>
      );
    } else if (status === 'partial') {
      return (
        <Badge className="bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800">
          PARTIAL
        </Badge>
      );
    }
    return (
      <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 dark:border-yellow-800">
        PENDING
      </Badge>
    );
  };

  // Calculate stats
  const paidInvoices = invoices.filter(inv => inv.payment_status === 'paid');
  const pendingInvoices = invoices.filter(inv => inv.payment_status === 'pending');
  const partialInvoices = invoices.filter(inv => inv.payment_status === 'partial');
  const taxExemptInvoices = invoices.filter(inv => inv.tax_exempt === true);
  
  const paidTotal = paidInvoices.reduce((sum, inv) => sum + inv.total, 0);
  const pendingTotal = pendingInvoices.reduce((sum, inv) => sum + inv.total, 0);
  const partialTotal = partialInvoices.reduce((sum, inv) => sum + inv.total, 0);
  const taxExemptTotal = taxExemptInvoices.reduce((sum, inv) => sum + inv.total, 0);

  const filteredInvoices = invoices
    .filter((invoice) => {
      const matchesSearch = 
        invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (invoice.company_name && invoice.company_name.toLowerCase().includes(searchTerm.toLowerCase()));
      
      // Project/Service/Truck search - searches in item descriptions AND truck number
      const matchesProject = !projectSearch || 
        (invoice.items && invoice.items.some(item => 
          item.description && item.description.toLowerCase().includes(projectSearch.toLowerCase())
        )) ||
        (invoice.truck_number && invoice.truck_number.toLowerCase().includes(projectSearch.toLowerCase()));
      
      const matchesStatus = statusFilter === "all" || invoice.payment_status === statusFilter;
      const matchesTaxExempt = !taxExemptFilter || invoice.tax_exempt === true;
      return matchesSearch && matchesProject && matchesStatus && matchesTaxExempt;
    })
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)); // Newest first

  const clearTaxExemptFilter = () => {
    setTaxExemptFilter(false);
    searchParams.delete('tax_exempt');
    setSearchParams(searchParams);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="invoices-loading">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="invoices-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
            {taxExemptFilter ? 'Tax Exempt Invoices' : 'Invoices'}
          </h1>
          <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            {taxExemptFilter 
              ? `${taxExemptInvoices.length} tax exempt invoices (${formatCurrency(taxExemptTotal)} total)`
              : `${invoices.length} total invoices`
            }
          </p>
        </div>
        <div className="flex items-center gap-2">
          {taxExemptFilter && (
            <Button 
              variant="outline"
              onClick={clearTaxExemptFilter}
              className={`${isDark ? 'border-purple-600 text-purple-400 hover:bg-purple-900/30' : 'border-purple-300 text-purple-600 hover:bg-purple-50'}`}
              data-testid="clear-tax-exempt-filter"
            >
              <FileCheck className="w-4 h-4 mr-2" />
              Tax Exempt Filter Active
              <span className="ml-2 text-xs">✕</span>
            </Button>
          )}
          <Link to={`${basePath}/invoices/new`}>
            <Button 
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold"
              data-testid="create-invoice-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Invoice
            </Button>
          </Link>
        </div>
      </div>

      {/* Tax Exempt Info Banner - shown when filter is active */}
      {taxExemptFilter && (
        <Card className={`${isDark ? 'bg-purple-900/20 border-purple-700' : 'bg-purple-50 border-purple-200'}`}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <FileCheck className="w-5 h-5 text-purple-600 mt-0.5" />
              <div>
                <h3 className={`font-semibold ${isDark ? 'text-purple-300' : 'text-purple-800'}`}>
                  What is Tax Exempt?
                </h3>
                <p className={`text-sm mt-1 ${isDark ? 'text-purple-200/70' : 'text-purple-700'}`}>
                  Tax exempt invoices are sales where no sales tax was charged. This typically applies to:
                </p>
                <ul className={`text-sm mt-2 space-y-1 ${isDark ? 'text-purple-200/70' : 'text-purple-700'}`}>
                  <li>• <strong>Resellers</strong> - They'll charge tax when selling to end customers</li>
                  <li>• <strong>Non-profits & Government</strong> - Organizations with tax exemption status</li>
                  <li>• <strong>Out-of-state customers</strong> - Depending on your state's nexus rules</li>
                </ul>
                <p className={`text-sm mt-2 font-medium ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                  💡 Tip: Keep exemption certificates on file for each tax-exempt customer in case of audits.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status summary cards removed per user request — Paid / Pending
          / Partial / Tax Exempt totals now live on the Financials page
          (with PIN protection), so showing them here was duplicate noise.
          The filter dropdown below still lets the user narrow by status. */}

      {/* Filters */}
      <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by invoice #, customer, or company..."
                value={searchTerm}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchTerm(value);
                  // Preserve search in URL for back navigation
                  if (value) {
                    searchParams.set('search', value);
                  } else {
                    searchParams.delete('search');
                  }
                  setSearchParams(searchParams, { replace: true });
                }}
                className={`pl-10 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}
                data-testid="search-input"
              />
            </div>
            <div className="relative w-full md:w-64">
              <Wrench className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-orange-400" />
              <Input
                placeholder="Search job, truck #..."
                value={projectSearch}
                onChange={(e) => {
                  const value = e.target.value;
                  setProjectSearch(value);
                  // Preserve search in URL for back navigation
                  if (value) {
                    searchParams.set('project', value);
                  } else {
                    searchParams.delete('project');
                  }
                  setSearchParams(searchParams, { replace: true });
                }}
                className={`pl-10 ${isDark ? 'bg-slate-700 border-slate-600 placeholder:text-slate-500' : 'bg-orange-50 border-orange-200 placeholder:text-orange-400'} ${projectSearch ? 'ring-2 ring-orange-400' : ''}`}
                data-testid="project-search-input"
              />
              {projectSearch && (
                <button 
                  onClick={() => setProjectSearch('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  ×
                </button>
              )}
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className={`w-full md:w-48 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`} data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {projectSearch && (
            <div className={`mt-3 text-sm ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
              <Wrench className="w-4 h-4 inline mr-1" />
              Showing invoices matching "{projectSearch}" ({filteredInvoices.length} found)
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoices Table */}
      <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className={isDark ? 'border-slate-700' : 'border-slate-200'}>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-slate-600'}>Invoice #</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-slate-600'}>Customer</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-slate-600'}>Date</TableHead>
                  <TableHead className={`text-right ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Total</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-slate-600'}>Status</TableHead>
                  <TableHead className={`text-right ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className={`text-center py-12 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>No invoices found</p>
                      <Link to={`${basePath}/invoices/new`}>
                        <Button variant="link" className="text-blue-600 mt-2">
                          Create your first invoice
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredInvoices.map((invoice) => (
                    <TableRow 
                      key={invoice.id} 
                      className={`table-row-hover cursor-pointer ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
                      data-testid={`invoice-row-${invoice.id}`}
                      onClick={() => navigate(`${basePath}/invoices/${invoice.id}`)}
                    >
                      <TableCell className="font-mono text-sm text-blue-600">{invoice.invoice_number}</TableCell>
                      <TableCell className={`font-medium ${isDark ? 'text-white' : 'text-slate-800'}`}>
                        <div>
                          {invoice.customer_name}
                          {invoice.company_name && (
                            <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                              {invoice.company_name}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className={isDark ? 'text-slate-400' : 'text-slate-500'}>
                        <div className="flex items-center gap-1 text-sm">
                          <Clock className="w-3 h-3" />
                          {new Date(invoice.created_at).toLocaleDateString()}
                        </div>
                      </TableCell>
                      <TableCell className={`text-right font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
                        {formatCurrency(invoice.total)}
                        {invoice.tax_exempt && (
                          <span className="block text-xs text-amber-600">Tax Exempt</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {getStatusBadge(invoice.payment_status)}
                          {invoice.tax_exempt && (
                            <Badge className="bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800 text-xs">
                              TAX EXEMPT
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <Link to={`${basePath}/invoices/${invoice.id}`}>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-slate-700"
                              data-testid={`view-invoice-${invoice.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </Link>
                          <Link to={`${basePath}/invoices/${invoice.id}/edit`}>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="hover:bg-green-50 hover:text-green-600 dark:hover:bg-slate-700"
                              data-testid={`edit-invoice-${invoice.id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          </Link>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(invoice.id, invoice.invoice_number)}
                            className="hover:bg-red-50 hover:text-red-600 dark:hover:bg-slate-700"
                            data-testid={`delete-invoice-${invoice.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
